import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiDollarSign,
  FiList,
  FiMapPin,
  FiX,
  FiArrowLeft,
  FiSend,
  FiZap,
  FiPlus,
  FiClock,
  FiFileText,
  FiCalendar,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

function ShortJobs() {
  const navigate = useNavigate();
  const { clientId: urlClientId } = useParams();

  // Get clientId from localStorage (same as PostNewJob)
  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  const clientId = getClientId();

  const [formData, setFormData] = useState({
    role: "",
    startTime: "",
    endTime: "",
    totalDuration: "",
    salary: "",
    experience: "",
    location: "",
    department: "",
    description: "",
    skills: "",
    jobType: "short",
    companyName: "",
    aboutCompany: "",
    expiryDate: "",
    startDate: "",
    endDate: "",
    startPeriod: "AM",
    endPeriod: "PM",
  });

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const roleRef = useRef(null);
  const deptRef = useRef(null);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [deptForm, setDeptForm] = useState({ name: "", description: "" });
  const [modalLoading, setModalLoading] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [experienceOptions, setExperienceOptions] = useState([]);
  const [showExpModal, setShowExpModal] = useState(false);
  const [newExpLabel, setNewExpLabel] = useState("");
  const [showExpDropdown, setShowExpDropdown] = useState(false);
  const expDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleRef.current && !roleRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
      if (deptRef.current && !deptRef.current.contains(event.target)) {
        setShowDeptDropdown(false);
      }
      if (expDropdownRef.current && !expDropdownRef.current.contains(event.target)) {
        setShowExpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (clientId) {
      fetchRoles();
      fetchDepartments();
      fetchEmployees();
      fetchExperienceOptions();
      fetchCompanyDefaults();
    }
  }, [clientId]);

  const fetchCompanyDefaults = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/settings`);
      if (res.data.success && res.data.settings) {
        const { companyName, aboutCompany } = res.data.settings;
        setFormData(prev => ({
          ...prev,
          companyName: companyName || "Timely Health",
          aboutCompany: aboutCompany || "",
        }));
      }
    } catch (err) {
      console.error("Failed to fetch company defaults:", err);
    }
  };

  // Automatic Duration Calculation
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      let [startH, startM] = formData.startTime.split(":").map(Number);
      let [endH, endM] = formData.endTime.split(":").map(Number);

      if (formData.startPeriod === "PM" && startH < 12) startH += 12;
      if (formData.startPeriod === "AM" && startH === 12) startH = 0;
      if (formData.endPeriod === "PM" && endH < 12) endH += 12;
      if (formData.endPeriod === "AM" && endH === 12) endH = 0;

      let startTotal = startH * 60 + startM;
      let endTotal = endH * 60 + endM;

      let diff = endTotal - startTotal;
      if (diff < 0) diff += 24 * 60;

      const hours = diff / 60;
      const formattedDuration = `${hours.toFixed(1).replace(/\.0$/, "")} Hours`;

      setFormData((prev) => ({ ...prev, totalDuration: formattedDuration }));
    }
  }, [formData.startTime, formData.endTime, formData.startPeriod, formData.endPeriod]);

  const fetchRoles = async () => {
    try {
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/roles/all?clientId=${clientId}`;
      } else {
        url = `${API_BASE_URL}/roles/all`;
      }
      const res = await axios.get(url);
      if (res.data.success || Array.isArray(res.data) || res.data.data) {
        let rolesData = Array.isArray(res.data) ? res.data : (res.data.data || res.data.roles || []);
        const uniqueRoles = rolesData.map((r, i) => ({ _id: r._id || i, name: r.name || r.title || r.role })).filter(r => r.name);
        setRoles(uniqueRoles);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  const fetchDepartments = async () => {
    try {
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/department/all?clientId=${clientId}`;
      } else {
        url = `${API_BASE_URL}/department/all`;
      }
      const res = await axios.get(url);
      if (res.data.success || Array.isArray(res.data) || res.data.data) {
        let deptData = [];
        if (Array.isArray(res.data)) deptData = res.data;
        else if (Array.isArray(res.data.data)) deptData = res.data.data;
        else if (Array.isArray(res.data.departments)) deptData = res.data.departments;
        
        const uniqueDepts = deptData.map((d, index) => {
            if (typeof d === 'string') return { _id: index, name: d };
            return { _id: d._id || index, name: d.name || d.departmentName || "Unknown Dept" };
        }).filter(d => d.name !== "Unknown Dept");
        
        setDepartments(uniqueDepts);
      }
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  };

  const fetchEmployees = async () => {
    setFetchingEmployees(true);
    try {
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/employees/get-employees?clientId=${clientId}`;
      } else {
        url = `${API_BASE_URL}/employees/get-employees`;
      }
      const res = await axios.get(url);
      const empData = Array.isArray(res.data) ? res.data : res.data.employees || res.data.data || [];
      setEmployees(empData);
    } catch (err) {
      console.error("Failed to fetch employees");
    } finally {
      setFetchingEmployees(false);
    }
  };

  useEffect(() => {
    if (employees.length > 0) {
      const employeeRoles = [...new Set(employees.map(emp => (emp.role || emp.position || emp.designation || "").trim()).filter(Boolean))];
      setRoles(prev => {
        const existingNames = new Set(prev.map(r => r.name.toLowerCase()));
        const newRoles = [...prev];
        employeeRoles.forEach((roleName, index) => {
          if (!existingNames.has(roleName.toLowerCase())) {
            newRoles.push({ _id: `emp-role-${index}`, name: roleName });
          }
        });
        return newRoles.sort((a, b) => a.name.localeCompare(b.name));
      });

      const employeeDepts = [...new Set(employees.map(emp => (emp.department || emp.dept || "").trim()).filter(Boolean))];
      setDepartments(prev => {
        const existingNames = new Set(prev.map(d => d.name.toLowerCase()));
        const newDepts = [...prev];
        employeeDepts.forEach((deptName, index) => {
          if (!existingNames.has(deptName.toLowerCase())) {
            newDepts.push({ _id: `emp-dept-${index}`, name: deptName });
          }
        });
        return newDepts.sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  }, [employees]);

  const handleCreateRole = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/roles/create`, { ...roleForm, clientId });
      if (response.data.success) {
        setMessage({ type: "success", text: `Role "${roleForm.name}" created!` });
        await fetchRoles();
        setFormData(prev => ({ ...prev, role: roleForm.name }));
        setShowRoleModal(false);
        setRoleForm({ name: "", description: "" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to create role" });
    } finally {
      setModalLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/department/create`, { ...deptForm, clientId });
      if (response.data.success) {
        setMessage({ type: "success", text: `Department "${deptForm.name}" created!` });
        await fetchDepartments();
        setFormData(prev => ({ ...prev, department: deptForm.name }));
        setShowDeptModal(false);
        setDeptForm({ name: "", description: "" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to create department" });
    } finally {
      setModalLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const fetchExperienceOptions = async () => {
    try {
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/experience-options/all?clientId=${clientId}`;
      } else {
        url = `${API_BASE_URL}/experience-options/all`;
      }
      const res = await axios.get(url);
      if (res.data.success) {
        setExperienceOptions(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch experience options:", err);
    }
  };

  const handleCreateExperience = async (e) => {
    e.preventDefault();
    if (!newExpLabel) return;
    setModalLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/experience-options/create`, { label: newExpLabel, clientId });
      if (res.data.success) {
        setExperienceOptions(prev => [res.data.data, ...prev]);
        setFormData(prev => ({ ...prev, experience: res.data.data.label }));
        setNewExpLabel("");
        setShowExpModal(false);
        setShowExpDropdown(false);
        setMessage({ type: "success", text: `Experience "${res.data.data.label}" created!` });
      }
    } catch (err) {
      console.error("Failed to create experience:", err);
      setMessage({ type: "error", text: "Failed to create experience" });
    } finally {
      setModalLoading(false);
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    if (!clientId) {
      setMessage({ type: "error", text: "Client not authenticated. Please login again." });
      setLoading(false);
      return;
    }

    try {
      const submissionData = {
        ...formData,
        startTime: `${formData.startTime} ${formData.startPeriod}`,
        endTime: `${formData.endTime} ${formData.endPeriod}`,
        clientId,
      };
      const response = await axios.post(`${API_BASE_URL}/jobs/create`, submissionData);
      if (response.data.success) {
        setMessage({ type: "success", text: "Short job posted successfully!" });
        setTimeout(() => navigate("/short-jobs"), 1800);
      }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to post job" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50/50 p-4 lg:p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Post Short Job</h1>
        <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest font-bold">
          Configure short-term recruitment details
        </p>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
          message.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-600 border border-rose-100"
        }`}>
          {message.type === "success" ? <FiCheckCircle size={18} /> : <FiX size={18} />}
          <span className="text-sm font-bold">{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <FiBriefcase size={20} className="text-indigo-600" />
                </div>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Job Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Job Position *</label>
                  <div className="relative" ref={roleRef}>
                    <FiBriefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="text" name="role" value={formData.role} onChange={(e) => { handleChange(e); setShowRoleDropdown(true); }}
                      onFocus={() => setShowRoleDropdown(true)} required autoComplete="off"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="e.g. ICU Nurse" />
                    {showRoleDropdown && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto no-scrollbar">
                        <button type="button" onClick={() => setShowRoleModal(true)} className="w-full px-4 py-3 text-left text-xs font-bold text-indigo-600 hover:bg-indigo-50 border-b border-gray-50 flex items-center gap-2">
                          <FiPlus /> Create New Role
                        </button>
                        {roles.filter(r => r.name.toLowerCase().includes(formData.role.toLowerCase())).map(role => (
                          <button key={role._id} type="button" onClick={() => { setFormData(p => ({ ...p, role: role.name })); setShowRoleDropdown(false); }}
                            className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                            {role.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Department *</label>
                  <div className="relative" ref={deptRef}>
                    <FiList className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="text" name="department" value={formData.department} onChange={(e) => { handleChange(e); setShowDeptDropdown(true); }}
                      onFocus={() => setShowDeptDropdown(true)} required autoComplete="off"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="Select Department" />
                    {showDeptDropdown && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto no-scrollbar">
                        <button type="button" onClick={() => setShowDeptModal(true)} className="w-full px-4 py-3 text-left text-xs font-bold text-indigo-600 hover:bg-indigo-50 border-b border-gray-50 flex items-center gap-2">
                          <FiPlus /> Create New Department
                        </button>
                        {departments.filter(d => d.name.toLowerCase().includes(formData.department.toLowerCase())).map(dept => (
                          <button key={dept._id} type="button" onClick={() => { setFormData(p => ({ ...p, department: dept.name })); setShowDeptDropdown(false); }}
                            className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                            {dept.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Start Date *</label>
                  <div className="relative">
                    <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">End Date *</label>
                  <div className="relative">
                    <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Start Time *</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <FiClock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                      <input type="time" name="startTime" value={formData.startTime} onChange={handleChange} required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium" />
                    </div>
                    <select
                      name="startPeriod"
                      value={formData.startPeriod}
                      onChange={handleChange}
                      className="px-3 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-bold bg-white"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">End Time *</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <FiClock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                      <input type="time" name="endTime" value={formData.endTime} onChange={handleChange} required
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium" />
                    </div>
                    <select
                      name="endPeriod"
                      value={formData.endPeriod}
                      onChange={handleChange}
                      className="px-3 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-bold bg-white"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Duration *</label>
                  <div className="relative">
                    <FiZap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="text" name="totalDuration" value={formData.totalDuration} readOnly required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-100 bg-gray-50 text-gray-500 cursor-not-allowed outline-none text-sm font-bold"
                      placeholder="Calculated automatically" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Salary / Pay *</label>
                  <div className="relative">
                    <FaRupeeSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                    <input type="text" name="salary" value={formData.salary} onChange={handleChange} required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="e.g. 2000 / Day" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Experience Required *</label>
                  <div className="relative" ref={expDropdownRef}>
                    <FiBriefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input
                      type="text"
                      name="experience"
                      value={formData.experience}
                      onChange={(e) => {
                        handleChange(e);
                        setShowExpDropdown(true);
                      }}
                      onFocus={() => setShowExpDropdown(true)}
                      required
                      autoComplete="off"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="Select or type experience"
                    />
                    {showExpDropdown && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto no-scrollbar">
                        <button
                          type="button"
                          onClick={() => setShowExpModal(true)}
                          className="w-full px-4 py-3 text-left text-xs font-bold text-indigo-600 hover:bg-indigo-50 border-b border-gray-50 flex items-center gap-2"
                        >
                          <FiPlus /> Create New Experience
                        </button>
                        {experienceOptions
                          .filter(opt => opt.label.toLowerCase().includes((formData.experience || "").toLowerCase()))
                          .map((opt) => (
                            <button
                              key={opt._id}
                              type="button"
                              onClick={() => {
                                setFormData(p => ({ ...p, experience: opt.label }));
                                setShowExpDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {opt.label}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Location *</label>
                  <div className="relative">
                    <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="text" name="location" value={formData.location} onChange={handleChange} required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="e.g. Hyderabad" />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Required Skills *</label>
                  <div className="relative">
                    <FiFileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                    <input type="text" name="skills" value={formData.skills} onChange={handleChange} required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="e.g. Nursing, Communication, Patient Care (separate by comma)" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FiFileText size={20} className="text-blue-600" />
                </div>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Job Description</h2>
              </div>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={6} required
                className="w-full p-6 rounded-2xl border border-gray-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all outline-none text-sm font-medium resize-none"
                placeholder="Detail the shift responsibilities and requirements..." />
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <FiCalendar size={20} className="text-amber-600" />
                </div>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Application Deadline</h2>
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Expiry Date</label>
                <div className="relative">
                  <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                  <input
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:border-amber-600 focus:ring-4 focus:ring-amber-600/5 transition-all outline-none text-sm font-medium"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2 italic">Applications will be disabled after this date.</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <FiBriefcase size={20} className="text-violet-600" />
                  </div>
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">Company Info</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingCompany(prev => !prev)}
                  className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-xl border transition-all ${
                    editingCompany
                      ? "bg-violet-600 text-white border-violet-600"
                      : "bg-violet-50 text-violet-600 border-violet-100 hover:bg-violet-100"
                  }`}
                >
                  {editingCompany ? <FiCheckCircle size={12} /> : <FiZap size={12} />}
                  {editingCompany ? "Done" : "Edit"}
                </button>
              </div>

              {editingCompany ? (
                <div className="space-y-5">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-4 focus:ring-violet-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="e.g. Timely Health"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">About Company</label>
                    <textarea
                      name="aboutCompany"
                      value={formData.aboutCompany}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-4 focus:ring-violet-600/5 transition-all outline-none text-sm font-medium resize-none"
                      placeholder="Brief description of your company, mission, and culture..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await axios.put(`${API_BASE_URL}/admin/settings/company`, {
                          companyName: formData.companyName,
                          aboutCompany: formData.aboutCompany,
                        });
                        setEditingCompany(false);
                      } catch (err) {
                        console.error("Failed to save company defaults", err);
                      }
                    }}
                    className="w-full py-3.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-widest transition-all shadow-lg shadow-violet-600/20"
                  >
                    Save as Default
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-violet-50/60 rounded-2xl border border-violet-100">
                    <p className="text-[9px] font-bold text-violet-400 uppercase tracking-widest mb-1">Company Name</p>
                    <p className="text-sm font-bold text-gray-800">{formData.companyName || "Timely Health"}</p>
                  </div>
                  {formData.aboutCompany && (
                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">About</p>
                      <p className="text-xs text-gray-600 leading-relaxed">{formData.aboutCompany}</p>
                    </div>
                  )}
                  <p className="text-[9px] text-gray-400 italic">Click Edit to customise for this job post or update defaults.</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sticky top-6">
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 px-6 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-all shadow-xl shadow-indigo-600/20 disabled:opacity-50">
                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><FiSend /> Post Short Job</>}
              </button>
              <button type="button" onClick={() => navigate(-1)} className="w-full mt-4 py-4 px-6 rounded-2xl bg-gray-50 text-gray-500 text-sm font-bold hover:bg-gray-100 transition-all">Cancel</button>
            </div>
          </div>
        </div>
      </form>

      {showRoleModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">Create New Role</h3>
              <button onClick={() => setShowRoleModal(false)} className="p-2 hover:bg-white rounded-xl transition-all"><FiX size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreateRole} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Role Name</label>
                <input type="text" value={roleForm.name} onChange={(e) => setRoleForm(p => ({ ...p, name: e.target.value }))} required
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 outline-none focus:border-indigo-600" placeholder="e.g. Senior Nurse" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Description</label>
                <textarea value={roleForm.description} onChange={(e) => setRoleForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 outline-none focus:border-indigo-600" rows="3" placeholder="Role description..." />
              </div>
              <button type="submit" disabled={modalLoading} className="w-full py-4 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all">
                {modalLoading ? "Creating..." : "Create Role"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showDeptModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">Create New Department</h3>
              <button onClick={() => setShowDeptModal(false)} className="p-2 hover:bg-white rounded-xl transition-all"><FiX size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreateDepartment} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Department Name</label>
                <input type="text" value={deptForm.name} onChange={(e) => setDeptForm(p => ({ ...p, name: e.target.value }))} required
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 outline-none focus:border-indigo-600" placeholder="e.g. Cardiology" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Description</label>
                <textarea value={deptForm.description} onChange={(e) => setDeptForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 outline-none focus:border-indigo-600" rows="3" placeholder="Department description..." />
              </div>
              <button type="submit" disabled={modalLoading} className="w-full py-4 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all">
                {modalLoading ? "Creating..." : "Create Department"}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {showExpModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">Create New Experience Level</h3>
              <button onClick={() => setShowExpModal(false)} className="p-2 hover:bg-white rounded-xl transition-all">
                <FiX size={20} className="text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleCreateExperience} className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Experience Label</label>
                <input
                  type="text"
                  value={newExpLabel}
                  onChange={(e) => setNewExpLabel(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 outline-none focus:border-indigo-600"
                  placeholder="e.g. 10+ Years"
                />
              </div>
              <button
                type="submit"
                disabled={modalLoading}
                className="w-full py-4 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all"
              >
                {modalLoading ? "Creating..." : "Create Experience"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortJobs;